import React, { memo, useEffect, useState } from "react";
import {
  OfficeEditFormValues,
  OfficeEmailRow,
  OfficePhoneRow,
  OfficeAddressRow,
  OfficeNoteRow,
} from "services/api/offices/types";
import { UserRow } from "services/api/users/types";
import {
  getOfficeEmailsAPI,
  getOfficePhonesAPI,
  getOfficeAddressesAPI,
  getOfficeNotesAPI,
  getOfficeUsersAPI,
} from "services/api/offices/api";
import { initialDataWithPagination } from "services/api/consts";
import { Loader } from "ui/components/Loader";
import { ReactSVG } from "react-svg";
import Badge from "ui/components/Badge/Badge";

interface OfficeSummaryStepProps {
  formValues: OfficeEditFormValues;
}

const OfficeSummaryStep: React.FC<OfficeSummaryStepProps> = ({ formValues }) => {
  const officeId = formValues.id;
  const [emails, setEmails] = useState<OfficeEmailRow[]>([]);
  const [phones, setPhones] = useState<OfficePhoneRow[]>([]);
  const [addresses, setAddresses] = useState<OfficeAddressRow[]>([]);
  const [notes, setNotes] = useState<OfficeNoteRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (officeId) fetchAll();
  }, [officeId]);

  const fetchAll = async () => {
    if (!officeId) return;
    setIsLoading(true);
    try {
      const [emailRes, phoneRes, addressRes, noteRes, usersRes] = await Promise.all([
        getOfficeEmailsAPI(initialDataWithPagination.pagination, officeId),
        getOfficePhonesAPI(initialDataWithPagination.pagination, officeId),
        getOfficeAddressesAPI(initialDataWithPagination.pagination, officeId),
        getOfficeNotesAPI(initialDataWithPagination.pagination, officeId),
        getOfficeUsersAPI(officeId),
      ]);
      setEmails(emailRes.data);
      setPhones(phoneRes.data);
      setAddresses(addressRes.data);
      setNotes(noteRes.data);
      setUsers(usersRes);
    } catch {
    }
    setIsLoading(false);
  };

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Pregled poslovnice</h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Provjerite unesene podatke prije spremanja poslovnice.
        </p>

        {isLoading ? (
          <Loader isLoading={true}> </Loader>
        ) : (
          <div className="border border-slate-200">
            {/* Osnovni podaci */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/company.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Osnovni podaci</h3>
                  <p className="text-xs text-slate-500">Pregled osnovnih informacija o poslovnici</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-sm">
                <div>
                  <span className="text-slate-400">Naziv:</span>
                  <span className="ml-2 text-slate-700 font-medium">{formValues.name || "-"}</span>
                </div>
              </div>
            </div>

            {/* Adrese */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/location.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Adrese <span className="text-xs text-slate-400 font-normal">({addresses.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene adrese poslovnice</p>
                </div>
              </div>
              {addresses.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih adresa</p>
              ) : (
                <div className="space-y-2">
                  {addresses.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{a.line1}{a.city ? `, ${a.postalCode} ${a.city}` : ""}</span>
                      {a.primary && <Badge text="Primarna" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/email.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Email adrese <span className="text-xs text-slate-400 font-normal">({emails.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene email adrese poslovnice</p>
                </div>
              </div>
              {emails.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih email adresa</p>
              ) : (
                <div className="space-y-2">
                  {emails.map((e) => (
                    <div key={e.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{e.emailAddress}</span>
                      {e.primary && <Badge text="Primarni" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Telefon */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/phone.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Telefoni <span className="text-xs text-slate-400 font-normal">({phones.length})</span></h3>
                  <p className="text-xs text-slate-500">Uneseni telefonski brojevi poslovnice</p>
                </div>
              </div>
              {phones.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih telefona</p>
              ) : (
                <div className="space-y-2">
                  {phones.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{p.phoneNumber}</span>
                      {p.primary && <Badge text="Primarni" variant="info" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Korisnici */}
            <div className="border-b border-slate-200 py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/users.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Korisnici <span className="text-xs text-slate-400 font-normal">({users.length})</span></h3>
                  <p className="text-xs text-slate-500">Korisnici povezani s poslovnicom</p>
                </div>
              </div>
              {users.length === 0 ? (
                <p className="text-sm text-slate-400">Nema povezanih korisnika</p>
              ) : (
                <div className="space-y-2">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span>{u.firstName} {u.lastName}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bilješke */}
            <div className="py-6 px-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <ReactSVG src="/media/icons/note.svg" className="w-5 h-5 stroke-slate-600" />
                </div>
                <div>
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Bilješke <span className="text-xs text-slate-400 font-normal">({notes.length})</span></h3>
                  <p className="text-xs text-slate-500">Unesene bilješke poslovnice</p>
                </div>
              </div>
              {notes.length === 0 ? (
                <p className="text-sm text-slate-400">Nema unesenih bilješki</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((n) => (
                    <div key={n.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="truncate">{n.content}</span>
                      {n.important && <Badge text="Važno" variant="warning" height="small" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(OfficeSummaryStep);
